import { ATTACHMENT_ACCEPT_TYPES } from '@bower/shared/schemas/upload.schema';

/**
 * Give a clipboard file a filename.
 *
 * A pasted screenshot arrives as a File with an empty `name` in every browser
 * we support, and `FormData.append` sends a nameless File as literally "blob"
 * — so the researcher's screenshot lands in the thread, and in their
 * workspace afterwards, called "blob". Name it after the moment it was pasted
 * instead.
 *
 * The suffix comes from `ATTACHMENT_ACCEPT_TYPES` rather than from splitting
 * the MIME string, so an unrecognised type yields no suffix at all instead of
 * an invented one ("image/svg+xml" would otherwise become ".svg+xml"). That
 * file then fails `pickAttachments` on both arms, which is the right answer:
 * naming runs BEFORE the gate precisely so the gate judges the real name.
 */
export function nameClipboardFile(file: File, index = 0): File {
  if (file.name) return file;

  // `hasOwnProperty`, not a bare index: `ATTACHMENT_ACCEPT_TYPES` is a plain
  // object, so a `file.type` of "constructor" or "toString" would otherwise
  // resolve to something off Object.prototype and the array destructure would
  // throw on a non-iterable. `isAcceptedAttachment` in @bower/shared guards the
  // same map the same way — this is its sibling and was missing the guard.
  const suffixes = Object.prototype.hasOwnProperty.call(ATTACHMENT_ACCEPT_TYPES, file.type)
    ? ATTACHMENT_ACCEPT_TYPES[file.type]
    : undefined;
  const [extension] = suffixes ?? [];

  const stamp = new Date().toISOString().replace(/[:.]/g, '-');
  // Two nameless files in ONE paste share a millisecond, so the stamp alone
  // gives them identical names and the tray shows one file twice.
  const ordinal = index > 0 ? `-${index + 1}` : '';

  return new File([file], `pasted-${stamp}${ordinal}${extension ?? ''}`, { type: file.type });
}
